import React, { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import Button from '../../util_components/button';
import SearchCombobox from '../../util_components/search_combobox/SearchCombobox';
import { filter_roles, role_key, role_matches } from './access_roles_filter';
import type { AccessRole } from './access_roles_filter';

interface Props<R extends AccessRole> {
  roles: R[];
  loading: boolean;
  // Receives the unfiltered role, so the editor always gets the full page list.
  on_edit: (role: R) => void;
}

const INPUT_STYLE: CSSProperties = {
  background: 'var(--surface-card)',
  color: 'var(--text-primary)',
  border: '1px solid var(--border-default)',
  borderRadius: 6,
  padding: '6px 10px',
  margin: 0,
  width: 280
};

const MUTED: CSSProperties = { color: 'var(--text-muted)' };

function AccessRolesTable<R extends AccessRole>({ roles, loading, on_edit }: Props<R>) {
  const [role, set_role] = useState('');
  const [page_query, set_page_query] = useState('');
  const rows = useMemo(() => filter_roles(roles, role, page_query), [roles, role, page_query]);
  const filtering = !!role || !!page_query.trim();

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <SearchCombobox<R>
          id="access-roles-role-filter"
          items={roles}
          get_key={role_key}
          get_label={role_key}
          matches={role_matches}
          value={role}
          on_change={set_role}
          label="Filter by role"
          placeholder="Filter by role"
          width={240}
        />
        <input
          type="text"
          aria-label="Filter by page path"
          placeholder="Filter by page path"
          autoComplete="off"
          value={page_query}
          onChange={(e) => set_page_query(e.target.value)}
          style={INPUT_STYLE}
        />
      </div>
      <div className="overflow-y-auto" style={{ maxHeight: '60vh' }}>
        <table className="cms-users-table w-full text-sm">
          <thead>
            <tr>
              <th className="p-3 font-medium text-left">Role</th>
              <th className="p-3 font-medium text-left">Users</th>
              <th className="p-3 font-medium text-left">Pages</th>
              <th className="p-3 font-medium text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ role: r, pages, hidden_count }) => (
              <tr key={r.access_type}>
                <td className="p-3 text-left align-top whitespace-nowrap">{r.access_type}</td>
                <td className="p-3 text-left align-top">{r.user_count}</td>
                <td className="p-3 text-left align-top">
                  {pages.length === 0 ? (
                    <span style={MUTED}>No pages</span>
                  ) : (
                    <div className="flex flex-wrap items-center gap-1">
                      {pages.map((page) => (
                        <span key={page} className="cms-page-chip rounded px-2 py-0.5 text-xs">
                          {page}
                        </span>
                      ))}
                      {hidden_count > 0 ? (
                        <span className="text-xs" style={MUTED}>
                          +{hidden_count} other {hidden_count === 1 ? 'page' : 'pages'}
                        </span>
                      ) : null}
                    </div>
                  )}
                </td>
                <td className="p-3 text-left align-top">
                  <Button type="secondary" onClick={() => on_edit(r)}>
                    Edit
                  </Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && !loading ? (
              <tr>
                <td className="p-4 text-left" colSpan={4} style={MUTED}>
                  {filtering ? 'No roles match' : 'No roles found'}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default AccessRolesTable;
